import React, { useState, useEffect, useRef } from 'react';
import { portfolioAPI } from '../../services/api';
import './Portfolio.css';

const COLOR_OPTIONS = [
  { label: 'Orange',  value: 'from-orange-400 to-orange-600' },
  { label: 'Red',     value: 'from-red-400 to-red-600' },
  { label: 'Blue',    value: 'from-blue-500 to-blue-700' },
  { label: 'Green',   value: 'from-green-400 to-green-600' },
  { label: 'Purple',  value: 'from-purple-400 to-purple-600' },
  { label: 'Teal',    value: 'from-teal-400 to-teal-600' },
  { label: 'Amber',   value: 'from-amber-400 to-amber-600' },
  { label: 'Pink',    value: 'from-pink-400 to-pink-600' },
  { label: 'Indigo',  value: 'from-indigo-500 to-indigo-700' },
  { label: 'Cyan',    value: 'from-cyan-400 to-cyan-600' },
];

const EMPTY_FORM = {
  title: '',
  category: '',
  client: '',
  description: '',
  features: '',
  tech: '',
  color: 'from-blue-500 to-blue-700',
  result: '',
  is_active: true,
  sort_order: 0,
};

const Portfolio = () => {
  const [items, setItems]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [showForm, setShowForm]   = useState(false);
  const [editing, setEditing]     = useState(null);
  const [formData, setFormData]   = useState(EMPTY_FORM);
  const [imageFile, setImageFile] = useState(null);        // File object
  const [imagePreview, setImagePreview] = useState(null);  // data URL for preview
  const [removeImage, setRemoveImage]   = useState(false); // flag to delete existing
  const [saving, setSaving]       = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => { fetchItems(); }, []);

  const fetchItems = async () => {
    try {
      const response = await portfolioAPI.adminGetAll();
      if (response.success) setItems(response.data);
    } catch {
      alert('Failed to fetch portfolio items');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImageFile(file);
    setRemoveImage(false);
    const reader = new FileReader();
    reader.onloadend = () => setImagePreview(reader.result);
    reader.readAsDataURL(file);
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreview(null);
    setRemoveImage(true);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const buildFormData = () => {
    const fd = new FormData();
    fd.append('title',       formData.title);
    fd.append('category',    formData.category);
    fd.append('client',      formData.client || '');
    fd.append('description', formData.description);
    fd.append('features',    formData.features || '');
    fd.append('tech',        formData.tech || '');
    fd.append('color',       formData.color);
    fd.append('result',      formData.result || '');
    fd.append('is_active',   formData.is_active ? '1' : '0');
    fd.append('sort_order',  formData.sort_order || 0);
    if (imageFile) fd.append('image', imageFile);
    if (removeImage) fd.append('remove_image', '1');
    return fd;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const fd = buildFormData();
    try {
      if (editing) {
        const response = await portfolioAPI.update(editing.id, fd);
        if (response.success) { alert('Portfolio item updated'); fetchItems(); resetForm(); }
      } else {
        const response = await portfolioAPI.create(fd);
        if (response.success) { alert('Portfolio item created'); fetchItems(); resetForm(); }
      }
    } catch (err) {
      const errors = err.response?.data?.errors;
      if (errors) {
        alert('Validation errors:\n' + Object.values(errors).flat().join('\n'));
      } else {
        alert('Failed to save portfolio item');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (item) => {
    setEditing(item);
    setFormData({
      title:       item.title,
      category:    item.category,
      client:      item.client || '',
      description: item.description,
      features:    Array.isArray(item.features) ? item.features.join(', ') : '',
      tech:        Array.isArray(item.tech) ? item.tech.join(', ') : '',
      color:       item.color,
      result:      item.result || '',
      is_active:   item.is_active,
      sort_order:  item.sort_order,
    });
    setImageFile(null);
    setImagePreview(null);
    setRemoveImage(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this portfolio item? This cannot be undone.')) return;
    try {
      const response = await portfolioAPI.delete(id);
      if (response.success) { alert('Portfolio item deleted'); fetchItems(); }
    } catch { alert('Failed to delete portfolio item'); }
  };

  const resetForm = () => {
    setFormData(EMPTY_FORM);
    setEditing(null);
    setImageFile(null);
    setImagePreview(null);
    setRemoveImage(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
    setShowForm(false);
  };

  // Existing image URL when editing (from API response)
  const existingImageUrl = editing?.image_url;
  const showExistingImage = existingImageUrl && !imagePreview && !removeImage;

  if (loading) return <div className="loading">Loading portfolio...</div>;

  return (
    <div className="portfolio-container">
      <div className="portfolio-header">
        <h2>Portfolio</h2>
        <button onClick={() => { resetForm(); setShowForm(v => !v); }} className="create-btn">
          {showForm && !editing ? 'Cancel' : '+ Add Project'}
        </button>
      </div>

      {showForm && (
        <div className="portfolio-form-card">
          <h3>{editing ? 'Edit' : 'Add New'} Portfolio Project</h3>
          <form onSubmit={handleSubmit} encType="multipart/form-data">

            {/* Title + Category */}
            <div className="form-row">
              <div className="form-field">
                <label>Project Title *</label>
                <input type="text" name="title" value={formData.title}
                  onChange={handleChange} placeholder="e.g., FreshCart" required />
              </div>
              <div className="form-field">
                <label>Category *</label>
                <input type="text" name="category" value={formData.category}
                  onChange={handleChange} placeholder="e.g., E-Commerce, ERP System" required />
              </div>
            </div>

            {/* Client + Sort Order */}
            <div className="form-row">
              <div className="form-field">
                <label>Client Name</label>
                <input type="text" name="client" value={formData.client}
                  onChange={handleChange} placeholder="e.g., Retail Chain, Ernakulam" />
              </div>
              <div className="form-field">
                <label>Sort Order</label>
                <input type="number" name="sort_order" value={formData.sort_order}
                  onChange={handleChange} min="0" />
              </div>
            </div>

            {/* Description */}
            <div className="form-row">
              <div className="form-field full-width">
                <label>Description *</label>
                <textarea name="description" value={formData.description}
                  onChange={handleChange} required rows="3"
                  placeholder="Brief description of the project..." />
              </div>
            </div>

            {/* Features + Tech */}
            <div className="form-row">
              <div className="form-field">
                <label>Key Features <span className="hint">(comma-separated)</span></label>
                <input type="text" name="features" value={formData.features}
                  onChange={handleChange} placeholder="Feature 1, Feature 2, Feature 3" />
              </div>
              <div className="form-field">
                <label>Tech Stack <span className="hint">(comma-separated)</span></label>
                <input type="text" name="tech" value={formData.tech}
                  onChange={handleChange} placeholder="React.js, Node.js, MongoDB" />
              </div>
            </div>

            {/* Color + Result */}
            <div className="form-row">
              <div className="form-field">
                <label>Card Color</label>
                <select name="color" value={formData.color} onChange={handleChange}>
                  {COLOR_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div className="form-field">
                <label>Result / Achievement</label>
                <input type="text" name="result" value={formData.result}
                  onChange={handleChange} placeholder="e.g., 3x increase in sales" />
              </div>
            </div>

            {/* Image Upload */}
            <div className="form-row">
              <div className="form-field full-width">
                <label>Project Image <span className="hint">(JPEG, PNG, WebP — max 3 MB)</span></label>

                {/* Existing image preview when editing */}
                {showExistingImage && (
                  <div className="image-preview-box">
                    <img src={existingImageUrl} alt="Current" />
                    <div className="image-preview-actions">
                      <span className="image-preview-label">Current image</span>
                      <button type="button" className="remove-image-btn" onClick={handleRemoveImage}>
                        ✕ Remove
                      </button>
                    </div>
                  </div>
                )}

                {/* New image preview */}
                {imagePreview && (
                  <div className="image-preview-box">
                    <img src={imagePreview} alt="Preview" />
                    <div className="image-preview-actions">
                      <span className="image-preview-label">New image (not saved yet)</span>
                      <button type="button" className="remove-image-btn" onClick={handleRemoveImage}>
                        ✕ Remove
                      </button>
                    </div>
                  </div>
                )}

                {/* Remove notice */}
                {removeImage && !imagePreview && (
                  <div className="remove-notice">Image will be removed on save.</div>
                )}

                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  onChange={handleImageChange}
                  className="file-input"
                />
              </div>
            </div>

            {/* Active checkbox */}
            <div className="form-row">
              <div className="form-field checkbox-field">
                <label>
                  <input type="checkbox" name="is_active" checked={formData.is_active}
                    onChange={handleChange} />
                  {' '}Active (visible on website)
                </label>
              </div>
            </div>

            <div className="form-actions">
              <button type="button" onClick={resetForm} className="cancel-btn">Cancel</button>
              <button type="submit" className="submit-btn" disabled={saving}>
                {saving ? 'Saving...' : (editing ? 'Update Project' : 'Add Project')}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Portfolio Table */}
      <div className="portfolio-table-wrapper">
        {items.length === 0 ? (
          <div className="empty-state"><p>No portfolio items yet. Add your first project!</p></div>
        ) : (
          <table className="portfolio-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Image</th>
                <th>Project</th>
                <th>Category</th>
                <th>Client</th>
                <th>Tech</th>
                <th>Status</th>
                <th>Order</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, idx) => (
                <tr key={item.id}>
                  <td>{idx + 1}</td>
                  <td>
                    {item.image_url ? (
                      <img src={item.image_url} alt={item.title} className="table-thumbnail" />
                    ) : (
                      <div className="no-image">No image</div>
                    )}
                  </td>
                  <td>
                    <div className="project-title-cell">
                      <div className={`color-dot bg-gradient-to-r ${item.color}`} />
                      <div>
                        <strong>{item.title}</strong>
                        {item.result && <p className="result-text">✓ {item.result}</p>}
                      </div>
                    </div>
                  </td>
                  <td><span className="category-badge">{item.category}</span></td>
                  <td className="muted">{item.client || '—'}</td>
                  <td>
                    <div className="tech-tags">
                      {Array.isArray(item.tech) && item.tech.slice(0, 3).map(t => (
                        <span key={t} className="tech-tag">{t}</span>
                      ))}
                    </div>
                  </td>
                  <td>
                    <span className={`status-badge ${item.is_active ? 'active' : 'inactive'}`}>
                      {item.is_active ? 'Active' : 'Hidden'}
                    </span>
                  </td>
                  <td className="center">{item.sort_order}</td>
                  <td>
                    <div className="action-btns">
                      <button onClick={() => handleEdit(item)} className="edit-btn">Edit</button>
                      <button onClick={() => handleDelete(item.id)} className="delete-btn">Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default Portfolio;
